<template>
    <div class="ls-flow--container-with-select">
        <LsTemplatedLoading :show="loading" :full-screen="true" test-id="loading" />
        <LsSelect
            v-model="selectedScheduleEventId"
            class="ls-flow--select"
            track-id="job_notes_view-schedule_event_options"
            name="selectedScheduleEventId"
            :options="scheduleEventOptions"
        />
        <div v-if="!loading" class="ls-flow--container">
            <div v-if="activeSortedComments.length" class="ls-flow--title">Job Notes</div>
            <form
                test-id="form_wrapper"
                class="ls-job-notes-view--form"
                novalidate
                @submit.prevent="createScheduleEventComment"
            >
                <div
                    v-if="activeSortedComments.length"
                    ref="commentsContainer"
                    class="ls-job-notes-view--comments-container"
                >
                    <div
                        v-for="(comment, idx) in activeSortedComments"
                        :key="comment.id"
                        ref="comment"
                        class="ls-job-notes-view--comments"
                    >
                        <LsComment
                            :comment="comment"
                            :is-last="activeSortedComments.length === idx + 1"
                        />
                    </div>
                </div>

                <LsContentPlaceholder
                    v-else
                    message="There are no notes yet for this job"
                    image="inactive"
                    fit-to-parent
                    class="ls-job-notes-view--placeholder"
                />

                <div class="ls-job-notes-view--dashed" />

                <LsInput
                    v-model="text"
                    placeholder="Add note here"
                    type="textarea"
                    track-id="job_notes_view-text"
                    name="text"
                    maxlength="1000"
                />
                <LsCheckboxes
                    v-model="is_internal"
                    track-id="job_notes_view-is_internal"
                    name="is_internal"
                    :options="[
                        {
                            value: false,
                            label: 'Hide from provider',
                        },
                    ]"
                />

                <LsButton
                    track-id="job_notes_view-add_note"
                    text="Add note"
                    :loading="isUpdating"
                    type="submit"
                    :disabled="!text"
                />
            </form>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { lsErrorService } from 'ls-vue-common';
import VueScrollTo from 'vue-scrollto';
import LsComment from '@/components/LsComment/LsComment.vue';
import { LsButton, LsInput, LsCheckboxes, LsSelect } from '@/components/common';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import LsContentPlaceholder from '@/components/LsContentPlaceholder/LsContentPlaceholder.vue';
import ScheduleEventsComments from '@/graphql/queries/ScheduleEventsComments.gql';
import CreateScheduleEventComment from '@/graphql/mutations/CreateScheduleEventComment.gql';
import { lsHelpers } from '@/helpers';
import { apolloClient } from '@/vue-apollo';

export default {
    name: 'JobNotesView',
    components: {
        LsComment,
        LsTemplatedLoading,
        LsButton,
        LsInput,
        LsCheckboxes,
        LsContentPlaceholder,
        LsSelect,
    },
    data() {
        return {
            loading: 0,
            isUpdating: false,

            selectedScheduleEventId: null,
            scheduleEventOptions: [],
            scheduleEventsComments: {},

            text: '',
            is_internal: [],

            variables: {
                customer_id: _.toNumber(this.$route.params.customer_id),
                property_id: _.toNumber(this.$route.params.property_id),
                schedule_id: _.toNumber(this.$route.params.schedule_id),
            },
        };
    },
    computed: {
        activeSortedComments() {
            return _.sortBy(
                _.filter(
                    this.scheduleEventsComments[this.selectedScheduleEventId].comments,
                    ({ deleted_at }) => {
                        return !deleted_at;
                    }
                ),
                'updated_at'
            );
        },
    },
    apollo: {
        scheduleEventsComments: {
            query: ScheduleEventsComments,
            variables() {
                return this.variables;
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
                this.$apollo.queries.scheduleEventsComments.skip = true;
                lsHelpers.goToParentView({ replace: true });
            },
            update(data) {
                if (!_.size(data.scheduleEventsComments)) {
                    lsErrorService.log({
                        error: { message: 'No recent jobs available' },
                        showAlert: true,
                    });
                    lsHelpers.goToParentView({ replace: true });
                    return;
                }
                this.scheduleEventOptions = _.map(
                    _.reverse(data.scheduleEventsComments),
                    (scheduleEvent) => {
                        return {
                            label:
                                lsHelpers.dateRange({
                                    start: scheduleEvent.customer_starts_at,
                                    end: scheduleEvent.customer_ends_at,
                                    format: '',
                                }) + ` (${_.capitalize(scheduleEvent.status)})`,
                            value: scheduleEvent.id,
                        };
                    }
                );

                const scheduleEventsById = _.keyBy(data.scheduleEventsComments, 'id');

                // if null, we're doing the initial query
                if (_.isNull(this.selectedScheduleEventId)) {
                    // if id qp exists
                    if (this.$route.query.id) {
                        // and we have it in the data pull, select it
                        if (scheduleEventsById[this.$route.query.id]) {
                            this.selectedScheduleEventId = this.$route.query.id;
                        } else {
                            // otherwise kick to dashboard
                            lsErrorService.log({
                                error: { message: 'Job not available' },
                                showAlert: true,
                            });
                            lsHelpers.goToParentView({ replace: true });
                            return;
                        }
                    } else {
                        this.selectedScheduleEventId = _.head(this.scheduleEventOptions).value;
                    }
                }

                return scheduleEventsById;
            },
            loadingKey: 'loading',
        },
    },
    methods: {
        createScheduleEventComment() {
            this.isUpdating = true;
            this.$apollo
                .mutate({
                    mutation: CreateScheduleEventComment,
                    variables: {
                        ...this.variables,
                        scheduleevent_id: _.toNumber(this.selectedScheduleEventId),
                        is_internal: Boolean(_.size(this.is_internal)),
                        text: this.text,
                    },
                })
                .then(({ data }) => {
                    this.text = '';
                    this.is_internal = [];

                    const { scheduleEventsComments } = apolloClient.readQuery({
                        query: ScheduleEventsComments,
                        variables: this.variables,
                    });

                    const idx = _.findIndex(scheduleEventsComments, {
                        id: this.selectedScheduleEventId,
                    });

                    scheduleEventsComments[idx].comments.push(data.createScheduleEventComment);

                    apolloClient.writeQuery({
                        query: ScheduleEventsComments,
                        variables: this.variables,
                        data: {
                            scheduleEventsComments,
                        },
                    });

                    // $nextTick to ensure dom has been updated with new comment
                    this.$nextTick(() => {
                        VueScrollTo.scrollTo(_.last(this.$refs.comment), 500, {
                            container: this.$refs.commentsContainer,
                        });
                    });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },
    },
};
</script>
<style lang="scss">
.ls-job-notes-view--form {
    .textarea:not([rows]) {
        min-height: 58px;
    }
}

.ls-job-notes-view--dashed {
    border-top: 1px dashed $primary;
}

.ls-job-notes-view--comments-container {
    margin-bottom: 0 !important;
    height: 420px;
    overflow: auto;
    width: 330px;
    @media (min-width: $desktop) {
        width: 720px;
    }
}

.ls-job-notes-view--comments {
    margin-bottom: 15px;
}

.ls-job-notes-view--placeholder {
    margin-top: 50px;
    padding-bottom: 95px !important;
}
</style>
